import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    Button,
    IconButton,
    TextField,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    withStyles
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { styles } from './styles';
import CoherentApi from '../../../collums-components/api/CoherentApi';
import CancelContinueModal from '../../../collums-components/components/common/CancelContinueModal';

const BRAND_LABELS = {
    visa: 'Visa',
    mc: 'Mastercard',
    amex: 'Amex',
    maestro: 'Maestro'
};

const SavedCards = ({ customer, classes }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [emailNotify, setEmailNotify] = useState(true);
    const [smsNotify, setSmsNotify] = useState(false);
    const [email, setEmail] = useState(customer?.email || '');
    const [phone, setPhone] = useState(customer?.mobilePhone || '');
    const [submitting, setSubmitting] = useState(false);
    const [deleteCard, setDeleteCard] = useState(null);

    const customerId = customer?._id || customer?.id;

    const fetchCards = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const response = await CoherentApi.getSavedCards(customerId);
            setCards(Array.isArray(response) ? response : []);
        } catch (e) {
            toastr.error('Failed to load saved cards');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCards();
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddCard = async () => {
        if (!emailNotify && !smsNotify) {
            toastr.error('Please select email or SMS');
            return;
        }
        if (emailNotify && !email?.trim()) {
            toastr.error('Please enter email address');
            return;
        }
        if (smsNotify && !phone?.trim()) {
            toastr.error('Please enter phone number');
            return;
        }
        setSubmitting(true);
        try {
            const result = await CoherentApi.createSavedCard({
                customerId,
                email: emailNotify ? email : undefined,
                phone: smsNotify ? phone : undefined,
                emailNotify,
                smsNotify
            });
            if (result?.success) {
                toastr.success('Card link sent', 'The customer will receive a link to add their card');
                setDialogOpen(false);
                fetchCards();
            } else {
                toastr.error('Failed to send card link');
            }
        } catch (e) {
            toastr.error('Error', e?.response?.data?.message || 'Failed to create saved card');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCard = async card => {
        try {
            const result = await CoherentApi.deleteSavedCard(card.id, customerId);
            if (result?.success) {
                toastr.success('Card deleted');
                setDeleteCard(null);
                fetchCards();
            } else {
                toastr.error('Failed to delete card');
            }
        } catch (e) {
            toastr.error('Error', e?.response?.data?.message || 'Failed to delete card');
        }
    };

    const getStatusLabel = status => {
        if (status === 'error' || status === 'ended') {
            return <span style={{ color: 'red', fontWeight: 'bold', fontSize: 14, marginLeft: 12 }}>EXPIRED</span>;
        }
        if (status === 'sent' || status === 'authorising') {
            return <span style={{ color: '#f0ad4e', fontSize: 14, marginLeft: 12 }}>PENDING</span>;
        }
        return null;
    };

    const getBrandLabel = brand => {
        if (!brand) return null;
        return BRAND_LABELS[brand.toLowerCase()] || brand.toUpperCase();
    };

    if (loading) {
        return (
            <Grid container className={classes.infoCard} justifyContent="center">
                <CircularProgress size={24} />
            </Grid>
        );
    }

    return (
        <>
            <Grid container className={classes.infoCard} spacing={1}>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        size="small"
                        style={{ textTransform: 'none', backgroundColor: '#2b78e4', color: '#fff' }}
                        onClick={() => {
                            setEmail(customer?.email || '');
                            setPhone(customer?.mobilePhone || '');
                            setEmailNotify(true);
                            setSmsNotify(false);
                            setDialogOpen(true);
                        }}
                    >
                        Add new card
                    </Button>
                </Grid>
                {cards.length === 0 && (
                    <Grid item xs={12}>
                        <Typography style={{ fontSize: 14, color: '#999', marginTop: 8 }}>No saved cards</Typography>
                    </Grid>
                )}
                {cards.map((card, index) => {
                    const hasExpiry = card.exp_month && card.exp_year;
                    return (
                        <Grid item xs={12} key={card.id} style={{ marginTop: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 'bold' }}>
                                <span>
                                    Card {index + 1}
                                    {getBrandLabel(card.card_brand) && (
                                        <span style={{ fontWeight: 'normal', marginLeft: 12 }}>
                                            {getBrandLabel(card.card_brand)}
                                        </span>
                                    )}
                                    {card.last_4 && (
                                        <span style={{ fontWeight: 'normal', marginLeft: 8 }}>{card.last_4}</span>
                                    )}
                                </span>
                                {getStatusLabel(card.status)}
                                <IconButton
                                    size="small"
                                    onClick={() => setDeleteCard(card)}
                                    title="Delete card"
                                    style={{ marginLeft: 16 }}
                                >
                                    <Delete style={{ color: '#f44336', fontSize: 20 }} />
                                </IconButton>
                            </div>
                            {hasExpiry && (
                                <div
                                    style={{
                                        marginLeft: 48,
                                        marginTop: 4,
                                        fontSize: 14
                                    }}
                                >
                                    Exp {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                                </div>
                            )}
                        </Grid>
                    );
                })}
            </Grid>

            <CancelContinueModal
                open={dialogOpen}
                setOpen={() => setDialogOpen(false)}
                title="Add new card"
                continueButtonText={submitting ? 'Sending...' : 'Send Link'}
                cancelButtonText="Cancel"
                onContinue={handleAddCard}
                disableButtons={
                    submitting ||
                    (!emailNotify && !smsNotify) ||
                    (emailNotify && !email.trim()) ||
                    (smsNotify && !phone.trim())
                }
                contentMaxWidth="500px"
                content={
                    <div style={{ width: '100%', textAlign: 'left' }}>
                        <Typography style={{ fontSize: 14, marginBottom: 16 }}>
                            Send a link to the customer to securely add their card details.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox checked={emailNotify} onChange={e => setEmailNotify(e.target.checked)} />
                            }
                            label="Send via Email"
                        />
                        {emailNotify && (
                            <TextField
                                fullWidth
                                label="Email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                margin="dense"
                                variant="outlined"
                            />
                        )}
                        <FormControlLabel
                            control={<Checkbox checked={smsNotify} onChange={e => setSmsNotify(e.target.checked)} />}
                            label="Send via SMS"
                        />
                        {smsNotify && (
                            <TextField
                                fullWidth
                                label="Phone"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                margin="dense"
                                variant="outlined"
                            />
                        )}
                    </div>
                }
            />

            {deleteCard && (
                <CancelContinueModal
                    open={!!deleteCard}
                    setOpen={() => setDeleteCard(null)}
                    title="Delete card"
                    contentText="Are you sure you want to delete this card?"
                    continueButtonText="Delete"
                    cancelButtonText="Cancel"
                    onContinue={() => handleDeleteCard(deleteCard)}
                />
            )}
        </>
    );
};

SavedCards.propTypes = {
    customer: PropTypes.object.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(SavedCards);
